import React from 'react'
import {
  Box,
  Button,
  Card,
  Container,
  Element,
  Heading,
  Level,
  Loader,
  Section,
} from 'react-bulma-components'
import { endOfMonth, startOfMonth, subMonths } from 'date-fns'
import Layout from '../../components/layout/layout'
import useUrlId from '../../hooks/use-url-id'
import { useWorkerQuery } from '../../queries/users'
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs'
import PageTitle from '../../components/pages/page-title'
import useStore from '../../store'
import { getWorkerEmployerRelationData } from '../../utils/users'
import { useIsInternalWorker } from '../../hooks/use-is-internal-worker'
import { Organization } from '../../api/organizations'
import Protected from '../../components/protected/protected'
import Avatar from '../../components/avatar/avatar'
import WorkerActions from '../../components/workers/worker-actions'
import WeekRangeFilter from '../../components/filters/fields/week-range'
import DailyWorkPeriodsTable from '../../components/work-periods/daily-work-periods-table'
import { useWeeksSummaryWorkPeriodsTable } from '../../hooks/use-weeks-summary-work-periods-table'
import { IsoDate } from '../../hooks/use-calendar'
import { dateToDateInputValue, endOfWeek, startOfWeek } from '../../utils/date'

/**
 * Worker details page: profile, work periods with week-range filter (up to one year), and actions.
 */
const WorkerDetails: React.FC = () => {
  const currentOrganization = useStore(state => state.session.currentOrganization as Organization)
  const { id } = useUrlId()
  const { data: worker } = useWorkerQuery(id as string)

  /**
   * Week filter: two date inputs; API uses Monday–Sunday for each bound.
   * Default is the current calendar week when the worker id is known.
   */
  const [weekRangeFromIso, setWeekRangeFromIso] = React.useState<IsoDate | ''>('')
  const [weekRangeToIso, setWeekRangeToIso] = React.useState<IsoDate | ''>('')

  React.useEffect(() => {
    if (!id) return
    setWeekRangeFromIso(dateToDateInputValue(startOfWeek(new Date())))
    setWeekRangeToIso(dateToDateInputValue(endOfWeek(new Date())))
  }, [id])

  /** Normalized inclusive week bounds for the weeks-summary API. */
  const summaryFromTo = React.useMemo(() => {
    if (!weekRangeFromIso || !weekRangeToIso) return null
    const from = startOfWeek(new Date(weekRangeFromIso))
    const to = endOfWeek(new Date(weekRangeToIso))
    if (from > to) return null
    return { from, to }
  }, [weekRangeFromIso, weekRangeToIso])

  const weeksSummaryParams =
    id && summaryFromTo
      ? {
          from: summaryFromTo.from,
          to: summaryFromTo.to,
          user: [id],
        }
      : undefined

  const {
    workPeriods: workPeriodsForTable,
    employerRules: employerRulesForTable,
    resolveEmployerRuleDay,
    isLoading: weeksSummaryLoading,
  } = useWeeksSummaryWorkPeriodsTable(weeksSummaryParams, {
    enabled: Boolean(id && summaryFromTo),
  })

  const { isInternalWorker, association } = useIsInternalWorker(worker)
  const isExternalWorker = !isInternalWorker
  const employerRelation = worker && getWorkerEmployerRelationData(worker, currentOrganization)

  const breadcrumbs = useBreadcrumbs(id)

  const isEmployerCollaborationInactive = Boolean(employerRelation?.collaboration?.isInactive)

  if (!worker) return null

  return (
    <Layout>
      <Section>
        <Container max={true}>
          {/* En-tête */}
          <Level>
            <Level.Side>
              <Breadcrumbs items={breadcrumbs} />
            </Level.Side>
          </Level>

          {/* Titre et statut */}
          <Level alignItems="flex-start" mb={6}>
            <Level.Side>
              <PageTitle title={<Avatar user={worker} />} subTitle={association?.internalRef} />
            </Level.Side>
          </Level>

          <Heading size={5}>Journées de travail</Heading>
          <Box mb={1}>
            <Element mb={4}>
              <Level mb={2}>
                <Level.Side>
                  <WeekRangeFilter
                    fromValue={weekRangeFromIso}
                    toValue={weekRangeToIso}
                    onFromChange={setWeekRangeFromIso}
                    onToChange={setWeekRangeToIso}
                    maxRangeDays={365}
                  />
                </Level.Side>
              </Level>
              <Element className="buttons">
                <Button
                  size="small"
                  onClick={() => {
                    const now = new Date()
                    setWeekRangeFromIso(dateToDateInputValue(startOfWeek(now)))
                    setWeekRangeToIso(dateToDateInputValue(endOfWeek(now)))
                  }}
                >
                  Semaine en cours
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    const lastMonth = subMonths(new Date(), 1)
                    setWeekRangeFromIso(dateToDateInputValue(startOfWeek(startOfMonth(lastMonth))))
                    setWeekRangeToIso(dateToDateInputValue(endOfWeek(endOfMonth(lastMonth))))
                  }}
                >
                  Dernier mois
                </Button>
              </Element>
            </Element>
            {weeksSummaryLoading ? (
              <Loader />
            ) : (
              <DailyWorkPeriodsTable
                workPeriods={workPeriodsForTable ?? []}
                employerRules={employerRulesForTable}
                resolveEmployerRuleDay={
                  employerRulesForTable.length ? resolveEmployerRuleDay : undefined
                }
                noDataMessage="Aucune journée de travail"
              />
            )}
          </Box>
          <Protected
            roles={[
              { role: 'employerMember', action: { resource: 'users', name: 'update' } },
              'interimAgencyMember',
            ]}
          >
            <Heading size={5} mt={6}>
              Informations & Actions
            </Heading>
            <Card>
              <Card.Content>
                <WorkerActions
                  worker={worker}
                  isEmployerCollaborationInactive={isEmployerCollaborationInactive}
                  isExternalWorker={isExternalWorker}
                  association={association}
                />
              </Card.Content>
            </Card>
          </Protected>
        </Container>
      </Section>
    </Layout>
  )
}

export default WorkerDetails

// Hooks
const useBreadcrumbs = (workerId?: string) =>
  React.useMemo(
    () => [
      {
        label: 'Travailleurs',
        to: '/settings/workers',
      },
      {
        label: 'Détails du travailleur',
      },
    ],
    [workerId],
  )
